import { FC, useMemo, useRef } from 'react';
import { useToggle } from 'react-use';
import { useTranslation } from 'next-i18next';
import { Modal, Table } from 'antd';

import { Button } from '@/shared/components';
import { Row } from '@/shared/components/layout';
import {
  getDtoRelatedToTargetDto,
  isDtoAlreadyInMap,
} from '@/shared/lib/helpers/getDtoRelatedToTargetDto';
import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { AssetDto } from '@/shared/lib/sj-orm/models/asset.dto';

import { useModalsStore } from '../../modals.store';
import { UnlinkAssetModalProps } from '../../types';

import { DataRow } from './types';
import { useColumnDefs } from './useColumnDefs';

export const UnlinkAssetModal: FC<UnlinkAssetModalProps> = ({
  document,
  selectedDocumentId,
  onDelete,
  onCancel,
}) => {
  const { closeModal } = useModalsStore();
  const { t: tc } = useTranslation('common');
  const { t: td } = useTranslation('documents', { keyPrefix: 'unlinkModal' });
  const sjDb = useSJDatabase();
  const columnDefs = useColumnDefs();
  const [isUnlinking, toggleLinked] = useToggle(true);

  const linkedDtos = useMemo(
    () => getDtoRelatedToTargetDto(sjDb, document, false) as Map<CollectionName, Set<AssetDto>>,
    [sjDb, document],
  );

  const initialLinkedAssets = useRef(linkedDtos.get(CollectionName.ASSETS));

  const toggleUnlink = async () => {
    const linkedAssets = linkedDtos.get(CollectionName.ASSETS);
    if (!initialLinkedAssets.current) return;

    if (isUnlinking && linkedAssets) {
      for (const linkedDto of linkedAssets) {
        await sjDb.collections.assets?.update({
          ...linkedDto,
          otherDocuments: linkedDto.otherDocuments?.filter(
            (documentId) => documentId !== selectedDocumentId,
          ),
        });
      }
    } else {
      await sjDb.collections.assets?.updateMany(Array.from(initialLinkedAssets.current));
    }

    toggleLinked();
  };

  const handleCancel = () => {
    closeModal();
    if (!onCancel) return;
    onCancel();
  };

  return (
    <Modal title={td('title')} open={true} onCancel={handleCancel} footer={null} centered>
      {initialLinkedAssets.current && initialLinkedAssets.current.size > 0 && (
        <Row direction="column" gapRow={16}>
          <Table<DataRow>
            style={{ marginTop: 24 }}
            rowKey="id"
            columns={columnDefs}
            virtual
            pagination={false}
            dataSource={Array.from(initialLinkedAssets.current ?? []).map((dto) => ({
              id: dto.id,
              key: dto.id,
              collectionName: CollectionName.ASSETS,
              object: td('dtos.asset'),
              name: dto.nickName ?? '',
              isLinked: isDtoAlreadyInMap(dto, linkedDtos),
            }))}
          />
          <Row direction="row" justifyContent="space-between" gapColumn={8} marginTop={16}>
            <Button
              style={{ flex: 1 }}
              size="middle"
              text={tc('Cancel')}
              type="default"
              onClick={handleCancel}
            />
            <Button
              style={{ flex: 1 }}
              size="middle"
              text={isUnlinking ? 'Unlink All' : 'Link back'}
              type="default"
              onClick={toggleUnlink}
            />
            <Button
              style={{ flex: 1 }}
              size="middle"
              text={tc('delete')}
              disabled={isUnlinking}
              onClick={onDelete}
            />
          </Row>
        </Row>
      )}
    </Modal>
  );
};
